/**
 * SLS WebTracking 日志上报封装
 *
 * 当前使用 STS 安全模式，通过后端 /sls/sts-token 接口获取临时凭证。
 * 临时凭证仅拥有向指定 project/logstore 写入日志的权限。
 *
 * 配置说明：
 * - host: SLS endpoint，如 'cn-shenzhen.log.aliyuncs.com'
 * - project: SLS Project 名称
 * - logstore: SLS Logstore 名称
 * - getSTSToken: 获取 STS 临时凭证的函数，返回 { AccessKeyId, AccessKeySecret, SecurityToken }
 *              不传入时 tracker 初始化但无法完成鉴权，sendSlsLog 会静默跳过
 */
import WebTrackerBrowser from '@aliyun-sls/web-track-browser';
import createStsPlugin from '@aliyun-sls/web-sts-plugin';

let tracker = null;
let isTrackerReady = false;

const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

function devLog(...args) {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
}

function devWarn(...args) {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.warn(...args);
    }
}

function devError(...args) {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.error(...args);
    }
}

/**
 * 初始化 SLS Tracker
 * @param {Object} options
 * @param {string} options.host
 * @param {string} options.project
 * @param {string} options.logstore
 * @param {Function} [options.getSTSToken] - 生产环境 STS 模式使用
 */
export function initSlsTracker(options = {}) {
    if (tracker) return tracker;

    const { host, project, logstore, getSTSToken } = options;

    if (!host || !project || !logstore) {
        devWarn('[SLS] host/project/logstore 未配置，SLS 上报未初始化');
        return null;
    }

    try {
        tracker = new WebTrackerBrowser({
            host,
            project,
            logstore
        });

        // STS 安全模式
        if (typeof getSTSToken === 'function') {
            const stsOpt = {
                accessKeyId: '',
                accessKeySecret: '',
                securityToken: '',
                refreshSTSToken: async () => {
                    const creds = await getSTSToken();
                    stsOpt.accessKeyId = creds.AccessKeyId;
                    stsOpt.accessKeySecret = creds.AccessKeySecret;
                    stsOpt.securityToken = creds.SecurityToken;
                }
            };
            tracker.useStsPlugin(createStsPlugin(stsOpt));
            devLog('[SLS] STS mode enabled');
        }

        isTrackerReady = true;
        devLog('[SLS] tracker initialized', { host, project, logstore, mode: getSTSToken ? 'STS' : 'no-auth' });
    } catch (err) {
        devError('[SLS] tracker init failed', err);
        isTrackerReady = false;
    }

    return tracker;
}

/**
 * 发送日志到 SLS
 * @param {Object} data
 */
export function sendSlsLog(data = {}) {
    if (!isTrackerReady || !tracker) {
        // 未初始化时静默跳过，避免报错影响业务
        return;
    }

    try {
        tracker.send(data);
    } catch (err) {
        devError('[SLS] send failed', err, data);
    }
}

/**
 * 获取 tracker 初始化状态
 */
export function isSlsReady() {
    return isTrackerReady;
}
